import React from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Gutter,
  PillsItem,
  Pills,
  SidedrawerUtil,
  Visible,
  useBreakpoint,
  useApi,
} from '@axiom/ui';
import type { Candidate, CandidateWorkFeedFormFilter } from '@axiom/validation';

import { WorkFeedFilterUtil } from '../../utils/work-feed-filter-util';
import type { FilterPill } from '../../types/work-feed.types';
import { WorkFeedFiltersSidedrawer } from '../WorkFeedFiltersSidedrawer/WorkFeedFiltersSidedrawer';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { useWorkFeedFilterPills } from './useWorkFeedFilterPills';

export type WorkFeedFilterPillsProps = {
  name?: string;
  candidate: Candidate;
  filters: CandidateWorkFeedFormFilter;
};

export const WorkFeedFilterPills = ({
  name = 'WORK_FEED_FILTER_PILLS',
  candidate,
  filters,
}: WorkFeedFilterPillsProps) => {
  const [, setSearchParams] = useSearchParams();
  const { isMobile } = useBreakpoint();
  const pills = useWorkFeedFilterPills(filters);

  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const onRemoveFilter = (filter: FilterPill) => {
    const category: string[] | null | undefined =
      filters[filter.name as keyof typeof filters];
    const index = category?.indexOf(filter.value) ?? -1;
    if (category && index > -1) {
      // Create new array without mutation
      const updatedCategory = [...category];
      updatedCategory.splice(index, 1);
      const updatedFilters = {
        ...filters,
        [filter.name]: updatedCategory,
      };
      WorkFeedFilterUtil.applyFilters(
        setSearchParams,
        candidate,
        taxonomy,
        updatedFilters
      );
    }
  };

  const handleClearAll = () => {
    WorkFeedFilterUtil.applyFilters(setSearchParams, candidate, taxonomy, null);
  };

  const handleOpenSidedrawer = () => {
    SidedrawerUtil.show({
      direction: 'left',
      only: 'smallScreen',
      name: 'work-feed-filters-side-drawer',
      content: <WorkFeedFiltersSidedrawer candidate={candidate} />,
    });
  };

  return (
    <div data-test={name}>
      <Visible only="smallScreen">
        <Button
          name="OPEN_FILTERS_SIDEDRAWER"
          pattern="secondary"
          onClick={handleOpenSidedrawer}
          fluid={isMobile}
        >
          Show filters
        </Button>
        <Gutter bottom="16px" />
      </Visible>
      {pills.length > 0 && (
        <Gutter bottom="16px">
          <Pills name="FILTER_PILLS_CONTAINER">
            {pills.map(pill => (
              <PillsItem
                name={pill.value}
                data-value={pill.value}
                key={pill.value}
                pattern="secondary"
                onDismiss={() => {
                  onRemoveFilter(pill);
                }}
              >
                {pill.label}
              </PillsItem>
            ))}
            <Button
              name="CLEAR_ALL"
              onClick={handleClearAll}
              variation="minimal"
              pattern="secondary"
            >
              Clear All
            </Button>
          </Pills>
        </Gutter>
      )}
    </div>
  );
};
